import { Toaster } from "sonner";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LanguageProvider, SEGMENTS } from "@/lib/i18n";
import { ImageFocusProvider } from "@/lib/imageFocus";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import EventsPage from "@/pages/EventsPage";
import EventDetail from "@/pages/EventDetail";
import AlbumPage from "@/pages/AlbumPage";
import Portfolio from "@/pages/Portfolio";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ClientGalleries from "@/pages/ClientGalleries";
import CollectionAlbums from "@/pages/CollectionAlbums";
import Restoration from "@/pages/Restoration";
import PhotoPackages from "@/pages/PhotoPackages";
import ClientGuide from "@/pages/ClientGuide";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import AdminDashboard from "@/pages/AdminDashboard";
import InvitePage from "@/pages/InvitePage";
import "@/App.css";

const PAGES = [
  ["events", <EventsPage />, [":slug", <EventDetail />], [":slug/:albumSlug", <AlbumPage />]],
  ["portfolio", <Portfolio />],
  ["about", <About />],
  ["services", <Services />],
  ["clientGalleries", <ClientGalleries />],
  ["collection", <CollectionAlbums />],
  ["restoration", <Restoration />],
  ["prints", <PhotoPackages />],
  ["guide", <ClientGuide />],
  ["privacy", <Privacy />],
  ["contact", <Contact />],
  ["login", <AuthPage mode="login" />],
  ["register", <AuthPage mode="register" />],
  ["account", <Account />],
  ["admin", <AdminDashboard />],
  ["invite", null, [":token", <InvitePage />]],
];

function langRoutes(lang) {
  const routes = [<Route key={`${lang}-home`} path={`/${lang}`} element={<Home />} />];
  PAGES.forEach(([key, element, ...children]) => {
    const seg = SEGMENTS[key][lang];
    if (element) routes.push(<Route key={`${lang}-${key}`} path={`/${lang}/${seg}`} element={element} />);
    children.forEach(([sub, subElement], i) =>
      routes.push(<Route key={`${lang}-${key}-${i}`} path={`/${lang}/${seg}/${sub}`} element={subElement} />)
    );
  });
  return routes;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/es" replace />} />
        {langRoutes("es")}
        {langRoutes("en")}
        <Route path="*" element={<Navigate to="/es" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <ImageFocusProvider>
            <AppRouter />
            <Toaster position="bottom-right" />
          </ImageFocusProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
